import { SIGNATURE_VIEWBOX, isSignatureValue } from "../../../../../components/SignaturePad/signature-path.js";
import { SignaturePad } from "../../../../../components/SignaturePad/index.js";
import { useFormBuilderFieldsContext } from "../../../contexts/FormBuilderFieldsContext.js";
import { InternalContentBadge } from "../../../shared/InternalContentBadge.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Text } from "@therms/atalaya";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/modules/FormBuilder/components/Fields/Signature/Form.tsx
/**
* The draft signature lives in local state, and everything the signer entered
* is written to the form — ink, a name, or both. Only a field with *nothing* in
* it writes `undefined`.
*
* Partial input is deliberately kept rather than discarded: a name typed
* without a signature (or a signature drawn without a name) is the signer's
* work, and dropping it loses data silently at submit time. Completeness is
* decided by `getSignatureFieldError` instead, which the shared validator calls
* for this type — see `docs/FormBuilder-signature-field-contract.md` §2.
*/
var SignatureFieldForm = ({ config }) => {
	const { errorsByFieldId, fieldValueChanged, valuesByFieldId } = useFormBuilderFieldsContext();
	const requireName = !!config.options.requireName;
	const [pathData, setPathData] = useState("");
	const [signerName, setSignerName] = useState("");
	/** Preserved across name edits so re-typing a name doesn't restamp the time. */
	const signedAtRef = useRef(void 0);
	const hydratedRef = useRef(false);
	const initializedRef = useRef(false);
	useEffect(() => {
		if (hydratedRef.current) return;
		const existing = valuesByFieldId[config.id];
		if (!isSignatureValue(existing)) return;
		hydratedRef.current = true;
		signedAtRef.current = existing.signedAt;
		setPathData(existing.d);
		setSignerName(existing.signerName || "");
	}, [config.id, valuesByFieldId]);
	useEffect(() => {
		if (!initializedRef.current) {
			initializedRef.current = true;
			return;
		}
		const trimmedName = requireName ? signerName.trim() : "";
		if (!pathData && !trimmedName) {
			signedAtRef.current = void 0;
			fieldValueChanged(config.id, void 0);
			return;
		}
		if (!pathData) signedAtRef.current = void 0;
		else if (!signedAtRef.current) signedAtRef.current = (/* @__PURE__ */ new Date()).toISOString();
		fieldValueChanged(config.id, {
			d: pathData,
			h: SIGNATURE_VIEWBOX.h,
			v: 1,
			w: SIGNATURE_VIEWBOX.w,
			...signedAtRef.current ? { signedAt: signedAtRef.current } : {},
			...trimmedName ? { signerName: trimmedName } : {}
		});
	}, [
		config.id,
		fieldValueChanged,
		pathData,
		requireName,
		signerName
	]);
	const handleChangePathData = useCallback((val) => {
		hydratedRef.current = true;
		setPathData(val);
	}, []);
	const error = errorsByFieldId[config.id];
	return /* @__PURE__ */ jsxs("div", {
		className: "space-y-xs",
		children: [/* @__PURE__ */ jsx(SignaturePad, {
			error: pathData ? void 0 : error,
			hint: /* @__PURE__ */ jsxs(Fragment$1, { children: [config.options.required && /* @__PURE__ */ jsx("span", {
				className: "text-color-critical text-sm font-light",
				children: "Required"
			}), config.internal && /* @__PURE__ */ jsx(InternalContentBadge, {})] }),
			label: /* @__PURE__ */ jsx(Fragment$1, { children: config.options.label || config.options.formLabel }),
			onChangeValue: handleChangePathData,
			subText: config.options.subText,
			value: pathData
		}), requireName && /* @__PURE__ */ jsx(Text, {
			error: pathData ? error : void 0,
			label: "Signer Name",
			name: `${config.name || config.id}-signer-name`,
			onChangeValue: setSignerName,
			value: signerName
		})]
	});
};
//#endregion
export { SignatureFieldForm };

//# sourceMappingURL=Form.js.map